import {
	ABOUT_MOBILE_DEALS,
	MOBILE_COMPARISON_HEADER_CONTENT,
} from "@/data/consts";
import { Article as ArticleType, Handset } from "@/types/index";
import Link from "next/link";
import * as React from "react";
import Image from "next/image";
import Search from "@/components/Search";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getOne } from "@/api/contentful";
import Article from "@/components/Article";
import { getHandsets } from "@/api/products";
import { Heading } from "@/components/Shared";
import { GetStaticProps, NextPage } from "next";
import PageSEO from "@/components/Shared/PageSEO";
import HeaderContent from "@/components/Comparison/HeaderContent";
import { HeaderContent as HeaderContentType } from "@/types/header";
import styles from "@/components/Comparison/Deals/styles.module.css";
import Head from "next/head";

const Breadcrumbs = [
	{
		title: "Home",
		href: "/",
		active: false,
	},
	{
		title: "Mobile",
		href: "/mobile",
		active: true,
	},
];

const Mobile: NextPage<{
	article: ArticleType;
	handsets: Array<Handset>;
	headerContent: HeaderContentType;
}> = ({ headerContent, article, handsets }) => {
	const [searchQuery, setSearchQuery] = React.useState("");
	const [filteredHandsets, setFilteredHandsets] = React.useState(handsets);

	const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
		const query = event.target.value.toLowerCase();
		setSearchQuery(query);

		const filtered = handsets.filter((handset) =>
			`${handset.manufacturer} ${handset.product_name} ${handset.model}`
				.toLowerCase()
				.includes(query)
		);
		setFilteredHandsets(filtered);
	};

	return (
		<div className="h-100 d-flex flex-column">
			<Head>
				<link rel="canonical" href="https://www.savely.co.uk/mobile" />
			</Head>
			<PageSEO
				title={"Mobile Comparison | Consumer Choices"}
				description={
					"Stop endlessly searching for mobile phone deals when you can compare multiple offers side by side using our simple comparison tools. Find the best plan for your needs, from budget-friendly options to newest flagship phone packages and even SIM-only deals!"
				}
				keywords={"Mobile phone deals, SIM only deals, Compare mobile offers"}
			/>
			<Header
				left={<HeaderContent data={headerContent} />}
				right={<></>}
				breadcrumbs={Breadcrumbs}
			/>
			<div id="deals" className="bg-blue">
				<div className="container mt-4" style={{ paddingBottom: "100px" }}>
					<div className="row w-100 py-3 d-flex justify-content-center justify-content-sm-start ">
						<div className="my-auto w-auto">
							<Heading title={"Find your phone:"} isBold={false} variant={3} />
						</div>
						<div className="w-auto">
							<Search handleSearch={handleSearch} />
						</div>
					</div>
					<div className="row">
						{filteredHandsets.length === 0 ? (
							<h3 className="text-center pt-5 mt-5">
								Sorry, there are no results. Try a different search term.
							</h3>
						) : (
							filteredHandsets.map((handset, i) => {
								return (
									<>
										<div className="d-flex col-12 col-sm-6 col-md-4 col-lg-3 text-center py-4">
											<Link
												href={`/mobile/${handset.manufacturer
													.replace(/\s+/g, "-")
													.toLowerCase()}/${handset.product_name
													.replace(/\s+/g, "-")
													.toLowerCase()}/${handset.model
													.replace(/\s+/g, "-")
													.toLowerCase()}`}
												className={`d-flex flex-column flex-grow-1 p-4 text-decoration-none text-reset ${styles.deal__container}`}
											>
												<h4 className="mb-3 h-40">
													{handset.manufacturer +
														" " +
														handset.product_name +
														" " +
														handset.model}
												</h4>
												<div className="mt-auto">
													<div
														style={{
															height: "1px",
															minHeight: "100px",
														}}
													>
														<div
															style={{
																height: "100%",
																width: "100%",
																position: "relative",
															}}
														>
															<Image
																src={handset.image_url}
																alt={handset.manufacturer + " " + handset.model}
																fill
																objectFit="contain"
															/>
														</div>
													</div>
													<div className="mt-4">
														<div>
															<strong>
																From &pound;{handset.deals[0].monthly_price} per
																month
															</strong>
														</div>
														<div>
															{Number(handset.deals[0].data_allowance) / 1000}
															GB data
														</div>
														<div>
															{handset.deals[0].minutes_allowance ===
															"UNLIMITED" ? (
																<>UNLIMITED Minutes</>
															) : (
																<>
																	{Number(handset.deals[0].minutes_allowance)}{" "}
																	Minutes
																</>
															)}
														</div>
														<button
															className={`${styles["broadband-affiliate__button"]} mt-3 mx-auto button d-flex align-items-center text-decoration-none`}
														>
															See Deals
														</button>
													</div>
												</div>
											</Link>
										</div>
									</>
								);
							})
						)}
					</div>
				</div>
			</div>
			<Article article={article} showAuthor={false} showSideBar={false} />
			<Footer />
		</div>
	);
};

export const getStaticProps: GetStaticProps = async () => {
	const handsets = await getHandsets();
	const article = await getOne(ABOUT_MOBILE_DEALS);
	const headerContent = await getOne(MOBILE_COMPARISON_HEADER_CONTENT);

	return {
		props: {
			headerContent,
			article,
			handsets: handsets.data,
		},
		revalidate: Number(process.env.REVALIDATE_CONTENTFUL),
	};
};

export default Mobile;
